// BankTransactionPage.tsx
import React, { FC, useState } from "react"
import { Form, Formik } from "formik"
import {
    IBankTransaction,
    initialBankTransactionValues,
    BankTransactionValidationSchema,
    tabOptions,
    IBAN_OPTIONS,
    BRANCH_OPTIONS,
    CASH_REGISTER_OPTIONS,

} from "./BankTransactionModel"
import { createBankTransaction, updateBankTransaction, deleteBankTransaction } from "./Api"
import FormTabHeader from "../../../../shared/components/FormTabHeader"
import TextInput from "../../../../shared/components/TextInput"
import SelectInput from "../../../../shared/components/SelectInput"
import { ConfirmModal } from "../../../../shared/components/ConfirmModal"
import { ToastContainer } from "react-toastify"
import { showErrorToast, showSuccessToast } from "../../../../shared/components/ToastUtils"
import "react-toastify/dist/ReactToastify.css"
import TextAreaInput from "../../../../shared/components/TextAreaInput"

export const BankTransactionPage: FC = () => {
    const [tab, setTab] = useState<string | "form">("form")
    const [loading, setLoading] = useState(false)
    const [selectedId, setSelectedId] = useState<string | null>(null)
    const [showModal, setShowModal] = useState(false)

    const handleSave = async (values: IBankTransaction, resetForm: () => void) => {
        setLoading(true)
        try {
            if (selectedId) {
                await updateBankTransaction({ ...values, id: selectedId })
                showSuccessToast("Banka işlemi başarıyla güncellendi")
            } else {
                await createBankTransaction(values)
                showSuccessToast("Banka işlemi başarıyla oluşturuldu")
            }
            resetForm()
            setSelectedId(null)
            setTab("form")
        } catch (err) {
            console.error(err)
            showErrorToast("Bir hata oluştu")
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        if (!selectedId) return
        try {
            await deleteBankTransaction(selectedId)
            showSuccessToast("Banka işlemi başarıyla silindi")
        } catch (err) {
            console.error(err)
            showErrorToast("Silinirken bir hata oluştu")
        } finally {
            setShowModal(false)
            setSelectedId(null)
        }
    }

    return (
        <>
            <Formik
                initialValues={initialBankTransactionValues}
                validationSchema={BankTransactionValidationSchema}
                enableReinitialize
                onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
            >
                {(formik) => {
                    const handleReset = () => {
                        formik.resetForm()
                        setSelectedId(null)
                    }

                    return (
                        <>
                            <FormTabHeader
                                formId="bankTransactionForm"
                                formikSubmit={formik.handleSubmit}
                                formikReset={handleReset}
                                loading={loading}
                                tab={tab}
                                setTab={setTab}
                                setSelectedId={setSelectedId}
                                tabs={tabOptions}
                                onExportExcel={() => console.log("Excel export")}
                            />

                            <div className="card mb-5">
                                <Form id="bankTransactionForm" className="form card-body">
                                    {tab === "form" && (
                                        <>
                                            {/* 1. Satır: Tarih — Makbuz Numarası */}
                                            <div className="row mb-6">
                                                <TextInput
                                                    name="date"
                                                    type="date"
                                                    label="Tarih"
                                                    wrapperClassName="col-md-6"
                                                />
                                                <TextInput
                                                    name="recieptNo"
                                                    label="Makbuz Numarası"
                                                    wrapperClassName="col-md-6"
                                                />
                                            </div>

                                            {/* 2. Satır: IBAN (Hesap No) — Kasa Kodu */}
                                            <div className="row mb-6">
                                                <SelectInput
                                                    name="iban"
                                                    label="IBAN ( Hesap No )"
                                                    options={IBAN_OPTIONS}
                                                    wrapperClassName="col-md-6"
                                                />
                                                <SelectInput
                                                    name="cashRegisterCode"
                                                    label="Kasa Kodu"
                                                    options={CASH_REGISTER_OPTIONS}
                                                    wrapperClassName="col-md-6"
                                                />
                                            </div>

                                            {/* 3. Satır: Banka — İşlem Şube */}
                                            <div className="row mb-6">
                                                <TextInput
                                                    name="bank"
                                                    label="Banka"
                                                    wrapperClassName="col-md-6"
                                                />
                                                <SelectInput
                                                    name="transactionBranch"
                                                    label="İşlem Şube"
                                                    options={BRANCH_OPTIONS}
                                                    wrapperClassName="col-md-6"
                                                />
                                            </div>

                                            {/* 4. Satır: Şube — İşlemi Yapan */}
                                            <div className="row mb-6">
                                                <TextInput
                                                    name="branch"
                                                    label="Şube"
                                                    wrapperClassName="col-md-6"
                                                />
                                                <TextInput
                                                    name="transactor"
                                                    label="İşlemi Yapan"
                                                    readOnly
                                                    wrapperClassName="col-md-6"
                                                />
                                            </div>

                                            {/* 5. Satır: Banka Tutarı (sağda TL) — Banka Kur (1,00) */}
                                            <div className="row mb-6">
                                                <TextInput
                                                    name="bankAmount"
                                                    type="number"
                                                    label="Banka Tutarı"
                                                    wrapperClassName="col-md-6"
                                                />
                                                <TextInput
                                                    name="bankKurus"
                                                    type="number"
                                                    label="Banka Kur"
                                                    step="0.0001"
                                                    min="0"
                                                    readOnly
                                                    wrapperClassName="col-md-6"
                                                />
                                            </div>

                                            {/* 6. Satır: Açıklama */}
                                            <div className="row mb-6">
                                                <TextAreaInput
                                                    name="description"
                                                    label="Açıklama"
                                                    wrapperClassName="col-12"
                                                />
                                            </div>
                                        </>
                                    )}
                                </Form>
                            </div>
                        </>
                    )
                }}
            </Formik>

            <ConfirmModal
                show={showModal}
                header="Silme Onayı"
                text="Bu banka işlemini silmek istediğinize emin misiniz?"
                handleClose={() => {
                    setShowModal(false)
                    setSelectedId(null)
                }}
                applyOnClick={handleDelete}
            />

            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    )
}

export default BankTransactionPage
